import { projectContactOntoRow } from "components/_dashboard/crm/panel/contactRowProjection";
import { readAmenities } from "components/_dashboard/crm/table/edit/crmCellEditModel";
import { getContactCustomerTypes } from "../../../../../../../@types/sharedSchema";
import type { RealEstateContact } from "../../../../../../../@types/sharedSchema";
import type { CrmCellSubject } from "components/_dashboard/crm/table/edit/crmCellEditModel";
import type { CrmContactRow } from "../../../../../../../@types/webotServer";

/**
 * A `RealEstateContact` as something the CRM's cell editor can edit.
 *
 * ── Why this is a projection and not a second model ──────────────────────────────
 *
 * The סקירה panels render the Firestore document; the editor was written for the Postgres
 * mirror's row. Rather than teach the editor a second vocabulary, the document is
 * projected onto the row shape it already speaks — `projectContactOntoRow` does exactly
 * that and is already the panel↔list bridge, so reusing it means the two sides of the CRM
 * cannot disagree about what a contact's name, budget or customer types are. Three of its
 * rules matter enough to name: it routes `customerTypes` through
 * `getContactCustomerTypes` (the legacy-scalar union, which old documents carry INSTEAD of
 * the array), it derives cities/neighborhoods from `interestedInLocations` rather than the
 * Cloud-Function mirrors that lag an edit, and it coerces the 338 string-valued budgets in
 * the corpus to numbers.
 *
 * ── And `doc`, which is the whole reason this file exists ────────────────────────
 *
 * The mirror row genuinely cannot express `mail`, `address`, `tehudatZehut`, the floor
 * pair, the amenity bag, or a budget's raw minimum — see `CRM_DOC_DIMENSIONS`. Those come
 * off the document directly, and attaching them is what makes those seven dimensions
 * offerable at all.
 *
 * ⚠️ THE BUDGET FIELDS HERE ARE RAW, AND THAT IS NOT THE SAME NUMBER `row.budgetMin`
 * HOLDS. The mirror column is `COALESCE(customerBudgetMin, customerBudget)`, so a contact
 * with one budget and no floor reads back from the row as a range it never entered. The
 * document says `null`, which is the truth. This is why the range dimensions are separate
 * from the table's scalar `budget` and must never be merged with it.
 *
 * ⚠️ `contactId` AND `ownerUserId` ARE SET EXPLICITLY. `projectContactOntoRow` returns a
 * `Partial<CrmContactRow>` and supplies neither, and both are load-bearing on the write:
 * the id addresses the document, and the owner is what the server INTERSECTS with the
 * caller's resolved scope. Omitting the owner silently targets the caller's own book,
 * which is right here only because these panels edit the caller's own contacts — and
 * stating it is what keeps that from being an accident.
 */
export function buildContactEditSubject(contact: RealEstateContact): CrmCellSubject {
  const projected = projectContactOntoRow(contact) as Partial<CrmContactRow>;

  return {
    ...(projected as CrmContactRow),
    contactId: contact.id ?? "",
    ownerUserId: contact.userId ?? "",
    // Present on the row type and not projected — the editor's status control reads it to
    // decide which SIDE's sub-statuses to offer (a buyer is never shown חתימת בלעדיות).
    customerTypes: getContactCustomerTypes(contact),
    doc: {
      mail: toText(contact.mail),
      address: toText(contact.address),
      tehudatZehut: toText(contact.tehudatZehut),
      floorsMin: toNumber(contact.floorsMin),
      floorsMax: toNumber(contact.floorsMax),
      squareMeterMin: toNumber(contact.squareMeterMin),
      squareMeterMax: toNumber(contact.squareMeterMax),
      amenities: readAmenities(contact as unknown as Record<string, unknown>),
      customerBudget: toNumber(contact.customerBudget),
      customerBudgetMin: toNumber(contact.customerBudgetMin),
      customerBudgetRent: toNumber(contact.customerBudgetRent),
      customerBudgetRentMin: toNumber(contact.customerBudgetRentMin)
    }
  };
}

const toText = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

/**
 * Numbers, tolerantly — the same coercion `contactRowProjection` and the server's
 * `asNumberOrNull` both apply, because 338 of 2,374 budgeted contacts store their budget
 * as a numeric STRING. A seed that read those as null would show an empty box over a real
 * value and then clear it on the first click-away.
 *
 * 0 is PRESERVED rather than nulled. Whether a stored 0 counts as "set" is a per-field
 * question the model answers (`isSetNumber` for money, `isSetFloor` for floors, and they
 * disagree on exactly this value) — deciding it here would take that choice away from the
 * one place that knows which field it is.
 */
const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "number" ? value : Number(String(value).replace(/[,\s₪]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
};
